import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { CcdlSpinnerService } from '@cpm-pr1618001/ng-design-lib';
import { catchError, map, of, switchMap } from 'rxjs';
import { UserDialogView } from '../../landing-page.modal';
import { LandingPageService } from '../../landing-page.service';
import { UserService } from '../../user/user.service';
import * as landingPageActions from '../actions/landing-page.actions';
import {
  ContactUsResponse,
  LandingHttpResponse,
  SignInResponse,
  SignUpResponse,
} from '../state/landing-page.state';

@Injectable({
  providedIn: 'root',
})
export class LandingPageEffects {
  constructor(
    private actions$: Actions,
    private userService: UserService,
    private landingPageService: LandingPageService,
    private ccdlSpinnerService: CcdlSpinnerService,
  ) {}

  signIn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(landingPageActions.signIn),
      switchMap((action) => {
        this.ccdlSpinnerService.open();
        return this.userService.signIn(action.signInDetails).pipe(
          map((signInResponse: SignInResponse) => {
            this.ccdlSpinnerService.close();
            return landingPageActions.signInSuccess({ signInResponse });
          }),
          catchError((error: Error) => {
            this.ccdlSpinnerService.close();
            return of(landingPageActions.signInError());
          }),
        );
      }),
    ),
  );

  signUp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(landingPageActions.signUp),
      switchMap((action) => {
        this.ccdlSpinnerService.open();
        return this.userService.signUp(action.signUpDetails).pipe(
          map((signUpResponse: SignUpResponse) => {
            this.ccdlSpinnerService.close();
            return landingPageActions.signUpSuccess({ signUpResponse });
          }),
          catchError((error: Error) => {
            this.ccdlSpinnerService.close();
            return of(landingPageActions.signUpError());
          }),
        );
      }),
    ),
  );

  signUpSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(landingPageActions.signUpSuccess),
      switchMap((action) =>
        of(
          landingPageActions.setActiveSignButton({
            activeSignButton: UserDialogView.SIGN_IN,
          }),
        ),
      ),
    ),
  );

  requestOTP$ = createEffect(() =>
    this.actions$.pipe(
      ofType(landingPageActions.requestOTP),
      switchMap((action) => {
        this.ccdlSpinnerService.open();
        return this.userService.requestOTP(action.emailId).pipe(
          map((val: LandingHttpResponse) => {
            this.ccdlSpinnerService.close();
            return landingPageActions.requestOTPSuccess();
          }),
          catchError((error: Error) => {
            this.ccdlSpinnerService.close();
            return of(landingPageActions.requestOTPError());
          }),
        );
      }),
    ),
  );

  reactivateAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(landingPageActions.reactivateAccount),
      switchMap((action) => {
        return this.userService.reactivateAccount(action.reactivateKey).pipe(
          map((response: LandingHttpResponse) => {
            return landingPageActions.reactivateAccountSuccess({ response });
          }),
          catchError((error: Error & { error?: { message?: string } }) => {
            return of(
              landingPageActions.reactivateAccountError({
                errorMessage:
                  error?.error?.message ??
                  'Unable to reactivate your account. Please try again later.',
              }),
            );
          }),
        );
      }),
    ),
  );

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(landingPageActions.resetPassword),
      switchMap((action) => {
        this.ccdlSpinnerService.open();
        return this.userService.resetPassword(action.fpDetails).pipe(
          map(() => {
            this.ccdlSpinnerService.close();
            return landingPageActions.resetPasswordSuccess();
          }),
          catchError((error: Error) => {
            this.ccdlSpinnerService.close();
            return of(landingPageActions.resetPasswordError());
          }),
        );
      }),
    ),
  );

  resetPasswordSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(landingPageActions.resetPasswordSuccess),
      switchMap((action) =>
        of(
          landingPageActions.setActiveSignButton({
            activeSignButton: UserDialogView.RESET_UPDATE,
          }),
        ),
      ),
    ),
  );

  submitContactUs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(landingPageActions.submitContactUs),
      switchMap((action) => {
        this.ccdlSpinnerService.open();
        return this.landingPageService
          .submitContactUs(action.contactUsDetails)
          .pipe(
            map((contactUsResponse: ContactUsResponse) => {
              this.ccdlSpinnerService.close();
              return landingPageActions.submitContactUsSuccess({
                contactUsResponse,
              });
            }),
            catchError((error: Error) => {
              this.ccdlSpinnerService.close();
              return of(landingPageActions.submitContactUsError());
            }),
          );
      }),
    ),
  );
}
